import { CAR_ROW, DRAW_DIST, HORIZON_Y, I0, ROAD_ROWS } from "../shared/constants";
import { TrackDef, sceneryAt } from "../shared/track";
import { PAL } from "./palette";

/**
 * Roadside furniture fixed in the world. Every circuit has its own set of props (see THEMES) and the lap is cut into
 * zones that favour different props, so each part of a circuit looks different from the previous one. The start/finish
 * straight always has grandstands and a gantry. Everything uses the road geometry, so it bends with the curves.
 * All artwork is original pixel art.
 */

export interface RoadGeom {
  x: Float32Array;
  hw: Float32Array;
  z: Float32Array;
  dy: Float32Array; // hills: vertical lift of each road row (pixels, negative = higher on screen)
}

const KZ = DRAW_DIST / (1 / I0 - 1 / 52);
const rowOfZ = (z: number) => 1 / (z / KZ + 1 / 52) - I0;

function hash(n: number): number {
  n = Math.imul(n ^ (n >>> 15), 2246822519);
  n = Math.imul(n ^ (n >>> 13), 3266489917);
  return (n ^ (n >>> 16)) >>> 0;
}
const mod = (a: number, b: number) => ((a % b) + b) % b;

type Ctx = CanvasRenderingContext2D;
type Painter = (ctx: Ctx, x: number, by: number, u: number, side: number, h: number) => void;

/** Rectangle in prop units: dx from the prop centre, dy above the ground. */
function R(ctx: Ctx, x: number, by: number, u: number, dx: number, dy: number, w: number, h: number, c: string) {
  ctx.fillStyle = c;
  ctx.fillRect(Math.round(x + dx * u), Math.round(by - (dy + h) * u), Math.max(1, Math.round(w * u)), Math.max(1, Math.round(h * u)));
}
/** Half ellipse standing at height dy. */
function D(ctx: Ctx, x: number, by: number, u: number, dx: number, dy: number, rx: number, ry: number, c: string) {
  ctx.fillStyle = c;
  const RY = Math.max(1, Math.round(ry * u));
  const RX = Math.max(1, rx * u);
  const cx = Math.round(x + dx * u), cy = Math.round(by - dy * u);
  for (let i = 0; i < RY; i++) {
    const half = Math.max(1, Math.round(RX * Math.sqrt(1 - ((RY - i - 0.5) / RY) ** 2)));
    ctx.fillRect(cx - half, cy - RY + i, half * 2, 1);
  }
}
/** Triangle with its base at height dy. */
function T(ctx: Ctx, x: number, by: number, u: number, dx: number, dy: number, hw: number, h: number, c: string) {
  ctx.fillStyle = c;
  const H = Math.max(1, Math.round(h * u));
  const cx = Math.round(x + dx * u), cy = Math.round(by - dy * u);
  for (let i = 0; i < H; i++) {
    const half = Math.max(1, Math.round((hw * u * (i + 1)) / H));
    ctx.fillRect(cx - half, cy - H + i, half * 2, 1);
  }
}

const [L, M, S, K] = PAL; // light, mid-light, mid-dark, dark

export const PROPS: Record<string, Painter> = {
  roosign: (c, x, y, u) => {
    R(c, x, y, u, -0.5, 0, 1.5, 8, K);
    for (let r = 0; r < 11; r++) {
      const half = 5 - Math.abs(r - 5);
      R(c, x, y, u, -half, 8 + r, half * 2 + 1, 1, L);
      R(c, x, y, u, -half, 8 + r, 1, 1, K);
      R(c, x, y, u, half, 8 + r, 1, 1, K);
    }
    R(c, x, y, u, -4, 12, 8, 1, K);
    R(c, x, y, u, -1, 11, 3, 3, K); // kangaroo: body, head, tail, legs
    R(c, x, y, u, 1, 13, 2, 2, K);
    R(c, x, y, u, -3, 11, 2, 1, K);
    R(c, x, y, u, -1, 10, 1, 1, K);
  },
  olive: (c, x, y, u) => {
    R(c, x, y, u, -1, 0, 3, 5, K);
    R(c, x, y, u, -2, 3, 1, 3, K);
    D(c, x, y, u, 0, 5, 8, 6, M);
    D(c, x, y, u, -2, 7, 4, 3, L);
    D(c, x, y, u, 3, 5, 3, 3, S);
  },
  umbrella: (c, x, y, u) => {
    R(c, x, y, u, -1, 0, 2, 13, K);
    R(c, x, y, u, -9, 12, 18, 2, S);
    D(c, x, y, u, 0, 13, 9, 4, S);
    D(c, x, y, u, -3, 13, 4, 2, M);
  },
  villa: (c, x, y, u) => {
    R(c, x, y, u, -9, 0, 18, 9, L);
    R(c, x, y, u, -9, 0, 1, 9, S);
    T(c, x, y, u, 0, 9, 11, 5, K);
    for (const dx of [-6, -1, 4]) R(c, x, y, u, dx, 3, 2, 4, K);
    R(c, x, y, u, -2, 0, 3, 3, K);
    R(c, x, y, u, 6, 9, 3, 6, L); // chimney
    R(c, x, y, u, 6, 14, 3, 1, K);
  },
  vines: (c, x, y, u) => {
    for (let i = 0; i < 4; i++) {
      R(c, x, y, u, -10 + i * 6, 0, 1, 6, K);
      R(c, x, y, u, -10 + i * 6 - 1, 5, 3, 2, S);
    }
    R(c, x, y, u, -10, 3, 19, 1, K);
  },
  bush: (c, x, y, u) => {
    D(c, x, y, u, 0, 0, 6, 5, S);
    D(c, x, y, u, -2, 2, 3, 3, M);
  },
  tree: (c, x, y, u) => {
    R(c, x, y, u, -1, 0, 2, 7, K);
    D(c, x, y, u, 0, 6, 8, 9, S);
    D(c, x, y, u, -3, 10, 3, 3, M);
  },
  gum: (c, x, y, u) => {
    R(c, x, y, u, -1, 0, 2, 13, K);
    D(c, x, y, u, 0, 12, 5, 7, S);
    R(c, x, y, u, 1, 14, 2, 1, M);
  },
  fence: (c, x, y, u) => {
    R(c, x, y, u, -9, 3, 19, 1, K);
    R(c, x, y, u, -9, 0, 1, 5, K);
    R(c, x, y, u, -3, 0, 1, 5, K);
    R(c, x, y, u, 3, 0, 1, 5, K);
    R(c, x, y, u, 9, 0, 1, 5, K);
  },
  billboard: (c, x, y, u, _s, h) => {
    R(c, x, y, u, -6, 0, 1, 7, K);
    R(c, x, y, u, 5, 0, 1, 7, K);
    R(c, x, y, u, -9, 7, 19, 9, K);
    R(c, x, y, u, -8, 8, 17, 7, L);
    const v = h % 3;
    if (v === 0) R(c, x, y, u, -8, 10, 17, 3, S);
    else if (v === 1) for (let i = 0; i < 4; i++) R(c, x, y, u, -7 + i * 4, 8, 2, 7, S);
    else {
      R(c, x, y, u, -6, 9, 5, 5, K);
      R(c, x, y, u, 1, 9, 6, 2, M);
      R(c, x, y, u, 1, 12, 6, 2, S);
    }
  },
  stand: (c, x, y, u, _s, h) => {
    R(c, x, y, u, -15, 0, 30, 10, K);
    for (let r = 0; r < 3; r++) for (let i = 0; i < 12; i++) R(c, x, y, u, -14 + i * 2.4, 1 + r * 3, 1.4, 1.6, (i + r + h) % 3 === 0 ? M : (i + r) % 2 ? L : S);
    R(c, x, y, u, -16, 10, 32, 2, S);
    R(c, x, y, u, -16, 12, 32, 1, K);
  },
  lamp: (c, x, y, u, s) => {
    R(c, x, y, u, -0.5, 0, 1.5, 17, K);
    R(c, x, y, u, s > 0 ? -4 : 0, 16, 5, 1, K);
    R(c, x, y, u, s > 0 ? -5 : 4, 14, 3, 2, L);
  },
  block: (c, x, y, u, _s, h) => {
    const H = 18 + (h % 3) * 6;
    R(c, x, y, u, -9, 0, 18, H, S);
    R(c, x, y, u, -10, H, 20, 2, K);
    for (let r = 0; r < Math.floor(H / 5); r++) for (let i = 0; i < 4; i++) R(c, x, y, u, -7 + i * 4, 2 + r * 5, 2, 3, (r + i + h) % 4 === 0 ? K : L);
  },
  palm: (c, x, y, u) => {
    R(c, x, y, u, -1, 0, 2, 12, K);
    R(c, x, y, u, -8, 12, 16, 1, S);
    R(c, x, y, u, -6, 13, 12, 1, S);
    R(c, x, y, u, -3, 14, 6, 1, S);
    R(c, x, y, u, -9, 10, 2, 2, S);
    R(c, x, y, u, 7, 10, 2, 2, S);
  },
  pine: (c, x, y, u) => {
    R(c, x, y, u, -1, 0, 2, 3, K);
    T(c, x, y, u, 0, 2, 7, 11, S);
    T(c, x, y, u, 0, 8, 5, 9, S);
    R(c, x, y, u, -1, 14, 1, 1, M);
  },
  cypress: (c, x, y, u) => {
    R(c, x, y, u, -1, 0, 2, 2, K);
    D(c, x, y, u, 0, 1, 3, 17, S);
    R(c, x, y, u, -1, 6, 1, 8, M);
  },
  rock: (c, x, y, u) => {
    D(c, x, y, u, 0, 0, 7, 5, S);
    D(c, x, y, u, -2, 0, 3, 3, M);
    R(c, x, y, u, 2, 0, 5, 1, K);
  },
  chalet: (c, x, y, u) => {
    R(c, x, y, u, -7, 0, 14, 8, M);
    T(c, x, y, u, 0, 8, 10, 6, K);
    R(c, x, y, u, -2, 0, 3, 5, K);
    R(c, x, y, u, 3, 3, 3, 3, L);
    R(c, x, y, u, -6, 3, 3, 3, L);
  },
  maison: (c, x, y, u, _s, h) => { // Quebec house: stone walls, steep mansard roof with dormers
    R(c, x, y, u, -8, 0, 16, 9, M);
    T(c, x, y, u, 0, 9, 11, 9, K);
    R(c, x, y, u, -3, 11, 6, 4, K);
    R(c, x, y, u, -2, 12, 4, 2, L);
    R(c, x, y, u, 4, 14, 1, 4, K);
    R(c, x, y, u, -1, 0, 3, 5, K);
    R(c, x, y, u, -6, 3, 3, 3, L);
    R(c, x, y, u, 4, 3, 3, 3, h & 1 ? L : K);
  },
  maple: (c, x, y, u) => {
    R(c, x, y, u, -1, 0, 2, 5, K);
    D(c, x, y, u, 0, 4, 8, 8, S);
    D(c, x, y, u, -3, 8, 4, 4, M);
  },
  jtree: (c, x, y, u) => {
    R(c, x, y, u, -1.5, 0, 3, 13, K);
    D(c, x, y, u, 0, 12, 9, 7, S);
    D(c, x, y, u, -3, 16, 4, 3, M);
    R(c, x, y, u, -5, 6, 1, 6, M);
    R(c, x, y, u, 4, 7, 1, 5, M);
  },
  fern: (c, x, y, u) => {
    D(c, x, y, u, 0, 0, 7, 4, S);
    D(c, x, y, u, 0, 0, 3, 8, M);
    D(c, x, y, u, -4, 0, 2, 6, S);
    D(c, x, y, u, 4, 0, 2, 6, S);
  },
  hut: (c, x, y, u) => {
    R(c, x, y, u, -7, 0, 14, 6, M);
    T(c, x, y, u, 0, 6, 10, 7, S);
    R(c, x, y, u, -1, 0, 3, 4, K);
    R(c, x, y, u, -9, 5, 1, 1, K);
  },
  torii: (c, x, y, u) => {
    R(c, x, y, u, -8, 0, 2, 15, K);
    R(c, x, y, u, 6, 0, 2, 15, K);
    R(c, x, y, u, -11, 14, 22, 2, K);
    R(c, x, y, u, -12, 16, 24, 1, S);
    R(c, x, y, u, -8, 10, 16, 1, K);
  },
  lantern: (c, x, y, u) => {
    R(c, x, y, u, -1, 0, 2, 5, K);
    R(c, x, y, u, -3, 5, 6, 4, K);
    R(c, x, y, u, -2, 6, 4, 2, L);
    R(c, x, y, u, -4, 9, 8, 1, K);
  },
  sakura: (c, x, y, u) => {
    R(c, x, y, u, -1, 0, 2, 8, K);
    D(c, x, y, u, 0, 7, 9, 8, M);
    for (const [a, b] of [[-5, 10], [2, 13], [-1, 9], [5, 10], [-3, 12]]) R(c, x, y, u, a, b, 1, 1, L);
  },
  conifer: (c, x, y, u) => {
    R(c, x, y, u, -1, 0, 2, 4, K);
    T(c, x, y, u, 0, 3, 7, 9, S);
    T(c, x, y, u, 0, 9, 6, 9, S);
    T(c, x, y, u, 0, 15, 5, 9, S);
    R(c, x, y, u, -1, 18, 1, 3, M);
  },
  birch: (c, x, y, u) => {
    R(c, x, y, u, -1, 0, 2, 14, L);
    R(c, x, y, u, -1, 3, 2, 1, K);
    R(c, x, y, u, -1, 8, 2, 1, K);
    D(c, x, y, u, 0, 13, 6, 7, M);
  },
  logs: (c, x, y, u) => {
    R(c, x, y, u, -7, 0, 14, 3, K);
    R(c, x, y, u, -5, 3, 10, 3, K);
    R(c, x, y, u, -3, 6, 6, 2, K);
    R(c, x, y, u, -7, 1, 1, 1, M);
    R(c, x, y, u, 6, 1, 1, 1, M);
    R(c, x, y, u, -5, 4, 1, 1, M);
  },
  windmill: (c, x, y, u) => {
    R(c, x, y, u, -3, 0, 6, 10, M);
    T(c, x, y, u, 0, 10, 4, 4, K);
    R(c, x, y, u, -9, 11, 18, 1, K);
    R(c, x, y, u, 0, 2, 1, 18, K);
    R(c, x, y, u, -1, 0, 2, 3, K);
  },
  house: (c, x, y, u) => {
    R(c, x, y, u, -8, 0, 16, 9, L);
    R(c, x, y, u, -8, 0, 1, 9, K);
    R(c, x, y, u, 7, 0, 1, 9, K);
    R(c, x, y, u, -8, 4, 16, 1, K);
    R(c, x, y, u, -1, 0, 2, 9, K);
    T(c, x, y, u, 0, 9, 10, 7, S);
    R(c, x, y, u, 3, 5, 3, 3, K);
    R(c, x, y, u, -6, 1, 3, 3, K);
  },
  haystack: (c, x, y, u) => {
    D(c, x, y, u, 0, 1, 7, 7, M);
    R(c, x, y, u, -7, 0, 14, 1, S);
    R(c, x, y, u, -2, 3, 1, 3, S);
  },
  apple: (c, x, y, u) => {
    R(c, x, y, u, -1, 0, 2, 5, K);
    D(c, x, y, u, 0, 5, 7, 7, S);
    R(c, x, y, u, -3, 8, 1, 1, L);
    R(c, x, y, u, 2, 6, 1, 1, L);
  },
  cactus: (c, x, y, u) => {
    R(c, x, y, u, -1, 0, 3, 14, S);
    R(c, x, y, u, -5, 6, 4, 2, S);
    R(c, x, y, u, -5, 6, 2, 6, S);
    R(c, x, y, u, 2, 8, 4, 2, S);
    R(c, x, y, u, 4, 8, 2, 6, S);
    R(c, x, y, u, 0, 2, 1, 10, M);
  },
  moai: (c, x, y, u) => { // an Easter Island statue: tall block head, heavy brow, long nose, thin mouth
    R(c, x, y, u, -3, 0, 7, 16, S);
    R(c, x, y, u, -4, 1, 1, 15, M); // shaded side
    R(c, x, y, u, -3, 11, 7, 1, M); // brow ridge
    R(c, x, y, u, 0, 6, 1, 5, M); // nose
    R(c, x, y, u, -1, 5, 3, 1, K); // mouth
    R(c, x, y, u, -3, 3, 7, 1, M); // chin line
    R(c, x, y, u, -2, 9, 1, 1, K);
    R(c, x, y, u, 1, 9, 1, 1, K); // eye hollows
  },
  dry: (c, x, y, u) => {
    D(c, x, y, u, 0, 0, 5, 5, M);
    R(c, x, y, u, -3, 2, 1, 1, K);
    R(c, x, y, u, 0, 3, 1, 1, K);
    R(c, x, y, u, 2, 1, 1, 1, K);
  },
  parasol: (c, x, y, u) => {
    R(c, x, y, u, -1, 0, 2, 13, K);
    D(c, x, y, u, 0, 13, 9, 4, S);
    R(c, x, y, u, -1, 13, 2, 4, L);
    R(c, x, y, u, -6, 13, 2, 2, L);
    R(c, x, y, u, 4, 13, 2, 2, L);
    R(c, x, y, u, -5, 0, 7, 1, M); // towel
    R(c, x, y, u, 6, 2, 5, 1, K); // beach chair
    R(c, x, y, u, 10, 2, 1, 5, K);
    R(c, x, y, u, 6, 0, 1, 2, K);
    R(c, x, y, u, 10, 0, 1, 2, K);
  },
  deckchair: (c, x, y, u) => {
    R(c, x, y, u, -4, 0, 1, 3, K);
    R(c, x, y, u, 3, 0, 1, 3, K);
    R(c, x, y, u, -4, 3, 8, 2, K);
    R(c, x, y, u, -3, 4, 6, 1, M);
    R(c, x, y, u, 2, 5, 2, 6, K);
    R(c, x, y, u, 3, 6, 1, 4, M);
  },
  boat: (c, x, y, u) => {
    R(c, x, y, u, -8, 1, 16, 3, K);
    R(c, x, y, u, -7, 0, 14, 1, K);
    R(c, x, y, u, 0, 4, 1, 16, K);
    for (let i = 0; i < 13; i++) R(c, x, y, u, 1, 5 + i, 6 - (i >> 1), 1, M); // mainsail
    for (let i = 0; i < 10; i++) R(c, x, y, u, -4 + (i >> 2), 5 + i, 4 - (i >> 2), 1, S); // jib
    R(c, x, y, u, 1, 19, 3, 1, K);
  },
  lifeguard: (c, x, y, u) => {
    R(c, x, y, u, -5, 0, 1, 7, K);
    R(c, x, y, u, 4, 0, 1, 7, K);
    R(c, x, y, u, -6, 7, 12, 6, M);
    R(c, x, y, u, -6, 7, 12, 1, K);
    R(c, x, y, u, -4, 9, 3, 2, K);
    R(c, x, y, u, 1, 9, 3, 2, K);
    T(c, x, y, u, 0, 13, 8, 3, S);
    R(c, x, y, u, 6, 13, 1, 6, K);
    R(c, x, y, u, 7, 17, 3, 2, S);
  },
  gullpost: (c, x, y, u) => {
    R(c, x, y, u, -1, 0, 2, 9, K);
    R(c, x, y, u, -3, 9, 6, 2, K); // gull perched on the post
    R(c, x, y, u, 2, 11, 2, 2, K);
    R(c, x, y, u, 4, 11, 1, 1, S);
    R(c, x, y, u, -4, 10, 2, 1, S);
    R(c, x, y, u, 7, 0, 1, 6, K);
    R(c, x, y, u, 6, 5, 3, 2, K);
  },
  plane: (c, x, y, u) => {
    R(c, x, y, u, -12, 3, 24, 4, K);
    R(c, x, y, u, -11, 4, 22, 2, M);
    for (let i = 0; i < 6; i++) R(c, x, y, u, -4 + i * 2, 5, 1, 1, K);
    R(c, x, y, u, 12, 4, 2, 2, K);
    R(c, x, y, u, -12, 7, 2, 6, K);
    R(c, x, y, u, -10, 7, 2, 3, K);
    R(c, x, y, u, -4, 2, 9, 2, S);
    R(c, x, y, u, -1, 0, 3, 2, K);
    R(c, x, y, u, -8, 0, 1, 3, K);
    R(c, x, y, u, 7, 0, 1, 3, K);
  },
  hangar: (c, x, y, u) => {
    D(c, x, y, u, 0, 0, 15, 11, K);
    D(c, x, y, u, 0, 0, 13, 9, M);
    R(c, x, y, u, -7, 0, 14, 6, K);
    R(c, x, y, u, -15, 0, 30, 1, K);
    R(c, x, y, u, 0, 0, 1, 6, S);
  },
  windsock: (c, x, y, u) => {
    R(c, x, y, u, -1, 0, 1, 14, K);
    R(c, x, y, u, 0, 12, 3, 3, K);
    R(c, x, y, u, 3, 12, 3, 3, M);
    R(c, x, y, u, 6, 13, 3, 2, K);
  },
  tower: (c, x, y, u) => {
    R(c, x, y, u, -2, 0, 4, 17, S);
    R(c, x, y, u, -2, 0, 1, 17, K);
    R(c, x, y, u, -1, 6, 2, 1, L);
    R(c, x, y, u, -1, 11, 2, 1, L);
    R(c, x, y, u, -6, 17, 12, 5, K);
    R(c, x, y, u, -5, 18, 10, 2, L);
    R(c, x, y, u, -7, 22, 14, 1, S);
    R(c, x, y, u, 0, 23, 1, 6, K);
    R(c, x, y, u, -2, 26, 5, 1, K);
  },
  heli: (c, x, y, u, _s, h) => {
    D(c, x, y, u, 0, 0, 9, 1, S);
    R(c, x, y, u, -5, 0, 10, 1, K);
    R(c, x, y, u, -3, 1, 1, 2, K);
    R(c, x, y, u, 2, 1, 1, 2, K);
    D(c, x, y, u, 0, 2, 5, 4, K);
    R(c, x, y, u, 2, 4, 3, 2, L);
    R(c, x, y, u, -12, 5, 8, 1, K);
    R(c, x, y, u, -12, 5, 1, 3, K);
    R(c, x, y, u, 0, 6, 1, 2, K);
    const half = h & 1 ? 12 : 9; // rotor length varies: it is spinning
    R(c, x, y, u, -half, 8, half * 2, 1, K);
  },
  acacia: (c, x, y, u) => {
    R(c, x, y, u, -1, 0, 2, 8, K);
    R(c, x, y, u, -3, 7, 2, 2, K);
    R(c, x, y, u, 1, 7, 2, 3, K);
    D(c, x, y, u, 0, 10, 12, 3, S);
    D(c, x, y, u, -4, 12, 5, 1, M);
  },
  baobab: (c, x, y, u) => {
    R(c, x, y, u, -5, 0, 10, 9, S);
    R(c, x, y, u, -5, 0, 2, 9, K);
    R(c, x, y, u, -7, 9, 2, 3, S);
    R(c, x, y, u, 5, 9, 2, 4, S);
    R(c, x, y, u, -1, 9, 2, 4, S);
    D(c, x, y, u, -6, 12, 3, 2, M);
    D(c, x, y, u, 6, 13, 3, 2, M);
    D(c, x, y, u, 0, 13, 3, 2, M);
  },
  giraffe: (c, x, y, u) => {
    for (const dx of [-4, -2, 2, 4]) R(c, x, y, u, dx, 0, 1, 6, K);
    R(c, x, y, u, -5, 6, 11, 4, M);
    R(c, x, y, u, -3, 7, 1, 1, K);
    R(c, x, y, u, 0, 8, 1, 1, K);
    R(c, x, y, u, 3, 7, 1, 1, K);
    R(c, x, y, u, 4, 9, 2, 9, M);
    R(c, x, y, u, 4, 12, 1, 1, K);
    R(c, x, y, u, 5, 16, 4, 2, M);
    R(c, x, y, u, 6, 18, 1, 1, K);
    R(c, x, y, u, -6, 8, 1, 3, K);
  },
  zebra: (c, x, y, u) => {
    for (const dx of [-4, -2, 2, 4]) R(c, x, y, u, dx, 0, 1, 4, K);
    R(c, x, y, u, -5, 4, 11, 4, K);
    R(c, x, y, u, -4, 5, 9, 2, L);
    for (let i = 0; i < 5; i++) R(c, x, y, u, -4 + i * 2, 4, 1, 4, K);
    R(c, x, y, u, 5, 7, 3, 4, K);
    R(c, x, y, u, 6, 10, 4, 2, K);
    R(c, x, y, u, -6, 5, 1, 3, K);
  },
  elephant: (c, x, y, u) => {
    for (const dx of [-7, -3, 1, 5]) R(c, x, y, u, dx, 0, 3, 5, K);
    R(c, x, y, u, -9, 5, 17, 6, S);
    R(c, x, y, u, 8, 4, 5, 7, S);
    R(c, x, y, u, 7, 6, 3, 5, K);
    R(c, x, y, u, 12, 0, 2, 8, S);
    R(c, x, y, u, 11, 3, 1, 2, K);
    R(c, x, y, u, -10, 6, 1, 4, S);
  },
  haussmann: (c, x, y, u, _s, h) => { // Paris apartment block: cream stone, wrought-iron balconies, zinc mansard roof with chimneys
    const H = 15 + (h % 3) * 4;
    R(c, x, y, u, -9, 0, 18, H, M);
    R(c, x, y, u, -10, H, 20, 3, K); // mansard
    R(c, x, y, u, -8, H + 3, 16, 1, K);
    R(c, x, y, u, -6 + (h % 4), H + 3, 2, 3, K); // chimney
    for (let r = 0; r < Math.floor((H - 2) / 5); r++) {
      for (let i = 0; i < 3; i++) R(c, x, y, u, -7 + i * 5, 2 + r * 5, 3, 4, K); // tall windows
      R(c, x, y, u, -8, 1 + r * 5, 16, 1, S); // balcony rail
    }
    R(c, x, y, u, -2, 0, 4, 5, L); // door
  },
  platane: (c, x, y, u) => { // plane tree of a Paris avenue: pale mottled trunk and a clipped crown
    R(c, x, y, u, -1, 0, 2, 7, L);
    R(c, x, y, u, 0, 2, 1, 2, S);
    R(c, x, y, u, -7, 7, 14, 5, S);
    D(c, x, y, u, 0, 11, 7, 4, S);
    R(c, x, y, u, -5, 8, 3, 2, M);
    R(c, x, y, u, 2, 10, 3, 2, M);
  },
  kiosk: (c, x, y, u) => { // Morris column covered with posters
    R(c, x, y, u, -2, 0, 5, 10, S);
    R(c, x, y, u, -2, 3, 5, 3, L);
    R(c, x, y, u, -1, 7, 3, 2, M);
    R(c, x, y, u, -3, 10, 7, 1, K);
    D(c, x, y, u, 0, 11, 3, 3, K);
    R(c, x, y, u, 0, 14, 1, 2, K);
  },
  cafe: (c, x, y, u) => { // corner cafe: striped awning and terrace tables
    R(c, x, y, u, -9, 0, 18, 9, M);
    R(c, x, y, u, -10, 8, 20, 3, K);
    for (let i = 0; i < 5; i++) R(c, x, y, u, -10 + i * 4, 8, 2, 3, L);
    R(c, x, y, u, -7, 1, 4, 5, K);
    R(c, x, y, u, 2, 1, 5, 5, L);
    R(c, x, y, u, -9, 0, 2, 2, K);
    R(c, x, y, u, 8, 0, 2, 2, K);
  },
  pin: (c, x, y, u) => { // Roman umbrella pine: bare trunk and a flat, wide crown
    R(c, x, y, u, -1, 0, 2, 11, K);
    D(c, x, y, u, 0, 9, 9, 4, S);
    R(c, x, y, u, -9, 10, 18, 2, S);
    D(c, x, y, u, -2, 11, 5, 2, M);
  },
  victorian: (c, x, y, u, _s, h) => { // Toronto Victorian row house: red brick, bay window, steep gable
    R(c, x, y, u, -8, 0, 16, 12, S);
    T(c, x, y, u, 0, 12, 9, 6, K);
    R(c, x, y, u, -6, 5, 5, 5, L); R(c, x, y, u, -5, 6, 3, 3, K); // bay window
    R(c, x, y, u, 2, 0, 4, 7, K); // door
    R(c, x, y, u, -7, 0, 14, 1, M);
    if (h & 1) R(c, x, y, u, 5, 12, 2, 4, K); // chimney
  },
  erable: (c, x, y, u) => { // maple: trunk and a round crown
    R(c, x, y, u, -1, 0, 2, 7, K);
    D(c, x, y, u, 0, 6, 8, 7, S);
    D(c, x, y, u, -2, 9, 4, 3, M);
  },
  tramway: (c, x, y, u) => { // TTC streetcar: long body, big windows, pantograph
    R(c, x, y, u, -11, 1, 22, 8, K);
    R(c, x, y, u, -11, 4, 22, 1, M);
    for (let i = 0; i < 5; i++) R(c, x, y, u, -9 + i * 4, 5, 3, 3, L);
    R(c, x, y, u, -9, 0, 3, 2, S); R(c, x, y, u, 6, 0, 3, 2, S);
    R(c, x, y, u, 0, 9, 1, 3, K); R(c, x, y, u, -2, 12, 5, 1, K);
  },
  palazzo: (c, x, y, u, _s, h) => { // ochre palazzo: shuttered windows, low terracotta roof
    R(c, x, y, u, -9, 0, 18, 14, M);
    T(c, x, y, u, 0, 14, 10, 4, K);
    for (let i = 0; i < 3; i++) { R(c, x, y, u, -7 + i * 5, 6, 3, 5, K); R(c, x, y, u, -7 + i * 5, 5, 3, 1, L); }
    R(c, x, y, u, -2, 0, 4, 5, K);
    R(c, x, y, u, -2, 5, 4, 1, L);
    if (h & 1) R(c, x, y, u, -8, 11, 4, 1, S);
  },
  colonne: (c, x, y, u, _s, h) => { // roman ruins: standing and broken columns
    R(c, x, y, u, -8, 0, 16, 2, S);
    for (let i = 0; i < 3; i++) {
      const H = i === 1 ? 7 : 11 - ((h >> i) % 3) * 2;
      R(c, x, y, u, -7 + i * 5, 2, 3, H, L);
      R(c, x, y, u, -7 + i * 5, 2, 1, H, M);
      if (H > 8) R(c, x, y, u, -8 + i * 5, 2 + H, 5, 1, S);
    }
    R(c, x, y, u, 5, 2, 3, 2, M);
  },
  fontaine: (c, x, y, u) => { // fountain with a basin and a tall jet
    R(c, x, y, u, -9, 0, 18, 3, M);
    R(c, x, y, u, -10, 3, 20, 1, S);
    R(c, x, y, u, -1, 4, 2, 7, L);
    D(c, x, y, u, 0, 11, 4, 3, L);
    R(c, x, y, u, -6, 4, 1, 3, L); R(c, x, y, u, 5, 4, 1, 3, L);
  },
  adobe: (c, x, y, u) => {
    R(c, x, y, u, -8, 0, 16, 8, M);
    R(c, x, y, u, -9, 8, 18, 1, L);
    R(c, x, y, u, -2, 0, 4, 5, K);
    R(c, x, y, u, 4, 3, 3, 3, K);
  },
};

interface Theme {
  props: string[];
  post: "post" | "lamp" | "stone" | "pole";
  tuft: "dash" | "dot";
}
export const THEMES: Record<string, Theme> = {
  trees: { props: ["gum", "tree", "bush", "fence", "billboard"], post: "post", tuft: "dash" },
  harbour: { props: ["gum", "roosign", "bush", "gum", "fence", "billboard"], post: "post", tuft: "dash" },
  city: { props: ["block", "palm", "block", "billboard", "lamp"], post: "lamp", tuft: "dot" },
  tuscany: { props: ["cypress", "olive", "umbrella", "villa", "vines"], post: "pole", tuft: "dash" },
  mountains: { props: ["pine", "rock", "chalet", "cypress", "fence"], post: "pole", tuft: "dash" },
  jungle: { props: ["palm", "jtree", "fern", "hut", "fern"], post: "post", tuft: "dash" },
  sunrise: { props: ["torii", "lantern", "sakura", "lantern", "billboard"], post: "post", tuft: "dash" },
  forest: { props: ["conifer", "birch", "logs", "conifer", "billboard"], post: "post", tuft: "dash" },
  hills: { props: ["windmill", "house", "haystack", "apple", "fence"], post: "post", tuft: "dash" },
  desert: { props: ["cactus", "rock", "dry", "adobe", "billboard"], post: "stone", tuft: "dot" },
  seaside: { props: ["palm", "parasol", "deckchair", "boat", "parasol", "lifeguard", "gullpost"], post: "post", tuft: "dash" },
  airport: { props: ["plane", "hangar", "windsock", "tower", "heli", "plane", "billboard"], post: "pole", tuft: "dot" },
  quebec: { props: ["maison", "maple", "pine", "maison", "maple", "fence", "billboard"], post: "lamp", tuft: "dash" },
  montreal: { props: ["block", "maple", "block", "billboard", "lamp", "maple"], post: "lamp", tuft: "dot" },
  paris: { props: ["haussmann", "platane", "kiosk", "haussmann", "cafe", "platane", "lamp"], post: "lamp", tuft: "dot" },
  toronto: { props: ["victorian", "erable", "tramway", "victorian", "erable", "lamp", "erable"], post: "lamp", tuft: "dot" },
  manx: { props: ["house", "rock", "bush", "fence", "haystack", "bush", "house", "fence"], post: "stone", tuft: "dash" },
  karting: { props: ["stand", "billboard", "lamp", "block", "bush", "fence", "lamp", "billboard"], post: "pole", tuft: "dot" },
  rome: { props: ["palazzo", "pin", "colonne", "palazzo", "fontaine", "cypress", "pin"], post: "lamp", tuft: "dot" },
  rio: { props: ["palm", "block", "parasol", "palm", "hut", "billboard", "lamp"], post: "lamp", tuft: "dot" },
  paques: { props: ["moai", "rock", "dry", "moai", "bush", "rock"], post: "stone", tuft: "dash" },
  hawaii: { props: ["palm", "rock", "parasol", "palm", "hut", "rock", "bush"], post: "stone", tuft: "dot" },
  tokyo: { props: ["block", "lantern", "billboard", "block", "lamp", "sakura", "billboard"], post: "lamp", tuft: "dot" },
  savanna: { props: ["acacia", "giraffe", "hut", "acacia", "zebra", "baobab", "elephant", "dry"], post: "stone", tuft: "dot" },
};

const POST_BASE = 1500;
const TUFT_BASE = 420;
const PROP_BASE = 1300;
const ZONES = 8;

/** Prop kind for a slot of the lap: each zone favours a main and a secondary prop of the circuit's set. */
function propFor(track: TrackDef, theme: Theme, slot: number, slotsPerLap: number, side: number) {
  const f = slot / slotsPerLap;
  if (f < 0.012 || f > 0.985) return { kind: "stand", h: hash(slot), density: 100 };
  const zone = Math.floor(f * ZONES);
  const zh = hash(track.id * 7919 + zone * 104729);
  const n = theme.props.length;
  const main = theme.props[zh % n];
  const alt = theme.props[(zh + 2 + (zh >>> 8) % (n - 1)) % n];
  const h = hash(slot * 31 + side * 977 + track.id);
  const density = zh % 5 === 0 ? 22 : 62; // some zones are open country
  return { kind: h % 4 ? main : alt, h, density };
}

/** k > 1 packs the posts, tufts and props closer together along the road (visual only): slower vehicle classes then feel faster. */
export function drawRoadside(ctx: Ctx, g: RoadGeom, track: TrackDef, pos: number, k = 1) {
  const TUFT_SPACING = TUFT_BASE / k, POST_SPACING = POST_BASE / k, PROP_SPACING = PROP_BASE / k;
  const themeAt = (wz: number) => THEMES[sceneryAt(track, wz)] ?? THEMES.trees; // a stage can change scenery along the way
  const rowAt = (z: number) => {
    const rf = rowOfZ(z);
    return rf > ROAD_ROWS - 0.5 ? -1 : Math.max(0, Math.round(rf));
  };
  const gy = (row: number) => HORIZON_Y + row + Math.round(g.dy[row]); // ground line of a road row, hills included
  const kerbOf = (row: number) => Math.max(1, Math.round(g.hw[row] * 0.07));
  const scOf = (row: number) => (row + 1) / (CAR_ROW + 1);

  // grass tufts / pebbles
  for (let n = Math.ceil((pos - 1500) / TUFT_SPACING); n * TUFT_SPACING < pos + DRAW_DIST; n++) {
    const row = rowAt(n * TUFT_SPACING - pos);
    if (row < 2) continue;
    const theme = themeAt(n * TUFT_SPACING);
    const h = Math.imul(n, 2654435761) >>> 0;
    const side = h & 1 ? 1 : -1;
    const f = 1.2 + (((h >>> 4) % 1000) / 1000) * 1.5;
    const sc = scOf(row);
    const x = Math.round(g.x[row] + side * g.hw[row] * f);
    ctx.fillStyle = theme.tuft === "dot" ? K : S;
    if (theme.tuft === "dot") {
      const s = Math.max(1, Math.round(1.6 * sc));
      ctx.fillRect(x, gy(row), s, Math.max(1, s - (s > 1 ? 1 : 0)));
    } else {
      const len = Math.max(1, Math.round(4 * sc));
      ctx.fillRect(x - (side > 0 ? 0 : len - 1), gy(row), len, 1);
      if (sc > 0.55) ctx.fillRect(x - (side > 0 ? 0 : len - 1), gy(row) + 1, len, 1);
    }
  }

  // marker posts on both verges
  for (let n = Math.ceil((pos - 1500) / (POST_SPACING / 2)); (n * POST_SPACING) / 2 < pos + DRAW_DIST; n++) {
    const row = rowAt((n * POST_SPACING) / 2 - pos);
    if (row < 3) continue;
    const theme = themeAt((n * POST_SPACING) / 2);
    const side = n & 1 ? 1 : -1;
    const sc = scOf(row);
    const x = Math.round(g.x[row] + side * (g.hw[row] + kerbOf(row) + 3 * sc));
    const by = gy(row);
    if (theme.post === "lamp") PROPS.lamp(ctx, x, by, sc, -side, 0);
    else if (theme.post === "stone") {
      D(ctx, x, by, sc, 0, 0, 3, 4, S);
      R(ctx, x, by, sc, -3, 0, 6, 1, K);
    } else {
      const tall = theme.post === "pole" ? 15 : 11;
      const w = Math.max(1, Math.round(2.4 * sc));
      const h = Math.max(2, Math.round(tall * sc));
      ctx.fillStyle = K;
      ctx.fillRect(x - (w >> 1), by - h + 1, w, h);
      ctx.fillStyle = (n >> 1) & 1 ? M : L;
      ctx.fillRect(x - (w >> 1), by - h + 1, w, Math.max(1, Math.round(h * 0.35)));
    }
  }

  // scenery props, far to near
  const slotsPerLap = Math.max(1, Math.floor(track.lapUnits / PROP_SPACING));
  const items: { row: number; draw: () => void }[] = [];
  for (let n = Math.ceil((pos - 2200) / PROP_SPACING); n * PROP_SPACING < pos + DRAW_DIST; n++) {
    const z = n * PROP_SPACING - pos;
    const row = rowAt(z);
    if (row < 4) continue;
    const slot = mod(n, slotsPerLap);
    const sc = scOf(row);
    for (const side of [-1, 1]) {
      const p = propFor(track, themeAt(n * PROP_SPACING), slot, slotsPerLap, side);
      if (hash(p.h * 3 + side) % 100 >= p.density) continue;
      const off = p.kind === "stand" ? 20 : 7 + (hash(p.h + 5) % 26);
      const x = Math.round(g.x[row] + side * (g.hw[row] + kerbOf(row) + off * sc));
      const by = gy(row);
      const painter = PROPS[p.kind];
      items.push({ row, draw: () => painter(ctx, x, by, sc * (p.kind === "stand" ? 0.9 : 0.85 + (p.h % 5) * 0.06), side, p.h) });
    }
  }
  items.sort((a, b) => a.row - b.row);
  for (const it of items) it.draw();

  // start/finish gantry spanning the road
  const next = Math.ceil((pos + 200) / track.lapUnits) * track.lapUnits;
  const row = rowAt(next - pos);
  if (row >= 3) {
    const sc = scOf(row);
    const kb = kerbOf(row);
    const x0 = Math.round(g.x[row] - g.hw[row] - kb - 2 * sc);
    const x1 = Math.round(g.x[row] + g.hw[row] + kb + 2 * sc);
    const by = gy(row);
    const w = Math.max(1, Math.round(2 * sc));
    const hh = Math.max(4, Math.round(20 * sc));
    ctx.fillStyle = K;
    ctx.fillRect(x0 - w, by - hh, w, hh);
    ctx.fillRect(x1, by - hh, w, hh);
    const bh = Math.max(2, Math.round(4 * sc));
    const cell = Math.max(2, Math.round(3 * sc));
    for (let cx = x0 - w, i = 0; cx < x1 + w; cx += cell, i++) {
      ctx.fillStyle = i & 1 ? L : K;
      ctx.fillRect(cx, by - hh, Math.min(cell, x1 + w - cx), bh);
    }
    ctx.fillStyle = K;
    ctx.fillRect(x0 - w, by - hh - 1, x1 - x0 + 2 * w, 1);
    ctx.fillRect(x0 - w, by - hh + bh, x1 - x0 + 2 * w, 1);
  }
}
